import { MouseEvent, useMemo } from 'react';
import { Card, CardHeader, CardContent, CardActions, Typography, Divider, Box, Theme } from '@mui/material';
import { IconButton } from '@components/iconButton';
import { Button } from '@components/button';
import { UserAccount } from '@components/container/UserAccount';
import { Flag, Stream, Times, UserCircle } from '@assets/svgs';
import { getNameInitials } from '@utils/regex';
import { shallow } from 'zustand/shallow';
import { Link as RouterLink } from 'react-router-dom';
import { useGetScreenSize } from '@utils/useGetScreenSize';
import { useStore } from '@store/GlobalStore';
import { adjustFontSize } from '@theme/styleOverrides/typography';
import { useApiEnvStore } from '@utils/api-client';
import { getToggleableFlags } from '@utils/featureFlags';

const cardStyle = {
  '&.MuiPaper-elevation0': {
    '&.MuiCard-root': {
      padding: 0,
      borderRadius: 'shape.borderRadius',
      backgroundColor: 'primary.dark',
      width: '400px',
    },
    '& .MuiCardHeader-root': {
      display: 'flex',
      padding: '8px 4px 8px 16px',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    '& .MuiCardHeader-content': {
      display: 'flex',
      flex: 'none',
    },
    '& .MuiCardHeader-action': {
      margin: '0px',
      flex: 'none',
    },
    '& .MuiCardContent-root': {
      padding: '20px 16px 16px 16px',
    },
  },
} as const;

interface ICardLogout {
  email?: string;
  firstName?: string;
  lastName?: string;
  brokerage?: {
    name: string;
    multiple: boolean;
  };
  onLogout: (event: MouseEvent<HTMLButtonElement>) => void;
  onUserCircleClick: () => void;
  onOpenEditRoles?: () => void;
  onOpenFeatureToggles?: () => void;
}

export const CardLogout = (props: ICardLogout) => {
  const { email, firstName, lastName, brokerage, onLogout, onUserCircleClick } = props;
  const [roles, userAuth, impersonation] = useStore(
    (state) => [state.roles, state.userAuth, state.impersonation],
    shallow,
  );
  const { isCustomerUser, hasPayerProfile } = userAuth;
  const { isMobileScreen } = useGetScreenSize();
  const [debugMode] = useApiEnvStore((state) => [state.debugMode], shallow);
  const onOpenEditRoles = props.onOpenEditRoles;
  const onOpenFeatureToggles = props.onOpenFeatureToggles;
  const toggleableFlags = getToggleableFlags();

  const originalAuth = useMemo(() => {
    return impersonation?.userOriginalAuthState?.userAuth ?? userAuth;
  }, [impersonation, userAuth]);

  const canEditRoles = useMemo(() => {
    return originalAuth?.isAdmin || originalAuth?.isPortalAdmin;
  }, [originalAuth]);

  const handleEditProfile = () => {
    onUserCircleClick();
  };

  const handleOpenEditRoles = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (onOpenEditRoles) {
      onOpenEditRoles();
    }
  };

  const handleOpenFeatureToggles = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (onOpenFeatureToggles) {
      onOpenFeatureToggles();
    }
  };

  return (
    <Card elevation={0} sx={cardStyle}>
      <CardHeader
        disableTypography
        title={<Typography variant='h5'>Profile</Typography>}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: '-2px' }}>
            <IconButton
              variant='circularDark'
              onClick={onUserCircleClick}
              sx={isMobileScreen ? { marginLeft: '-56px', color: 'text.main' } : { color: 'text.main' }}
            >
              {isMobileScreen ? <Times /> : <UserCircle id='userCircleIcon' width='20px' height='20px' />}
            </IconButton>
          </Box>
        }
      />
      <Divider sx={{ borderColor: 'background.dark' }} />
      <CardContent>
        <UserAccount
          boxProps={{
            sx: {
              display: 'flex',
              alignItems: 'center',
            },
          }}
          avatarProps={{
            sx: {
              backgroundColor: 'accent.secondary',
              width: '64px',
              height: '64px',
              fontSize: ({ palette }: Theme) => `${adjustFontSize(30, palette.fontSize)}px`,
              marginRight: '16px',
            },
            children: getNameInitials(`${firstName} ${lastName}`),
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography
              variant='body'
              sx={{
                fontWeight: 'bold',
                color: 'white100',
              }}
            >
              {firstName} {lastName}
            </Typography>
            <Typography
              variant='bodySmall'
              sx={{
                color: 'white100',
              }}
            >
              {email}
            </Typography>
            {brokerage && (
              <Typography
                variant='bodySmall'
                sx={{
                  color: 'white100',
                }}
              >
                {brokerage.name} {brokerage.multiple ? ' and more' : ''}
              </Typography>
            )}
          </Box>
        </UserAccount>
        {debugMode && (
          <Box sx={{ mt: 2 }}>
            {canEditRoles ? (
              <IconButton
                aria-label='Edit Roles'
                variant='circularDark'
                onClick={handleOpenEditRoles}
                sx={{ color: 'text.main' }}
              >
                <Stream width='18px' height='18px' />
              </IconButton>
            ) : null}
            <Typography variant='caption' sx={{ color: 'text.subdued' }}>
              Roles: {roles?.length ? roles.join(', ') : 'None'}
            </Typography>
          </Box>
        )}
        {toggleableFlags.length > 0 && (
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
            <IconButton
              aria-label='Toggle Features'
              variant='circularDark'
              onClick={handleOpenFeatureToggles}
              sx={{ color: 'text.main' }}
            >
              <Flag width='16px' height='16px' />
            </IconButton>
            <Typography variant='caption' sx={{ color: 'text.subdued' }}>
              Toggle Features
            </Typography>
          </Box>
        )}
      </CardContent>
      <CardActions sx={{ paddingBottom: '8px', justifyContent: 'center' }}>
        {isCustomerUser && !hasPayerProfile ? null : (
          <RouterLink to='/settings/profile' onClick={() => handleEditProfile()}>
            <Button variant='text'>Edit Profile</Button>
          </RouterLink>
        )}
        <Button onClick={onLogout} variant='text'>
          Log out
        </Button>
      </CardActions>
      {/* Modal rendered by parent (ProfileBadge) to avoid unmount on popover close */}
    </Card>
  );
};
